import { useLingui } from "@lingui/react/macro";
import { Alert, List, Text, Title } from "@mantine/core";
import { createFileRoute } from "@tanstack/react-router";

import { DocHeading } from "@/components/docs/DocHeading.tsx";
import { DocsContent } from "@/components/docs/DocsContent.tsx";

export const Route = createFileRoute("/docs/downloads/underlying-data")({
  component: RouteComponent,
});

function RouteComponent() {
  const { t } = useLingui();

  return (
    <DocsContent>
      <Title order={1} size="h2">{t`Underlying data`}</Title>
      <Text>
        {t`REALity provides access to data from official sources. For property information, the primary source is Lantmäteriet.`}
      </Text>

      <DocHeading order={2} size="h3">{t`Lantmäteriet services`}</DocHeading>
      <Text>
        {t`The underlying information may include data from several Lantmäteriet services, such as services related to:`}
      </Text>
      <List withPadding>
        <List.Item>{t`General property information.`}</List.Item>
        <List.Item>{t`Property designations.`}</List.Item>
        <List.Item>{t`Addresses.`}</List.Item>
        <List.Item>{t`Registration information.`}</List.Item>
        <List.Item>{t`Rights.`}</List.Item>
        <List.Item>{t`Property-regulating provisions.`}</List.Item>
        <List.Item>{t`Taxation.`}</List.Item>
        <List.Item>{t`Maps and geographical information.`}</List.Item>
      </List>
      <Text>
        {t`Different services provide different kinds of information and may have different prices and conditions. A single search in REALity may therefore involve one or more underlying data requests.`}
      </Text>

      <DocHeading order={2} size="h3">{t`Other sources`}</DocHeading>
      <Text>
        {t`REALity can also support data from other official sources. The platform is designed so that a data source can be changed or supplemented without requiring every end-user application to be rebuilt.`}
      </Text>

      <DocHeading order={2} size="h3">{t`Important limitations`}</DocHeading>
      <List withPadding>
        <List.Item>{t`Data may not be real-time in every part of the system.`}</List.Item>
        <List.Item>{t`Different datasets may have different update cycles.`}</List.Item>
        <List.Item>{t`Data coverage can vary between geographical areas and information types.`}</List.Item>
        <List.Item>{t`A search result is only as complete as the underlying source and selected service.`}</List.Item>
        <List.Item>{t`Data should be interpreted in accordance with the source provider’s documentation and terms.`}</List.Item>
        <List.Item>{t`REALity does not replace professional assessment or legal review.`}</List.Item>
      </List>
      <Alert variant="light" color="blue">
        {t`If exact currency or legal validity is important, check the applicable source information and requirements for the assignment.`}
      </Alert>
    </DocsContent>
  );
}
